"use client"

import { IconPhone, IconPhoneCall, IconPhoneOff, IconExternalLink, IconUser } from "@tabler/icons-react"
import { formatDistanceToNow } from "date-fns"
import { useTranslations } from "next-intl"

import { Button } from "@/components/ui/button"
import type { IncomingCallInfo } from "@/types"

interface IncomingCallPopupProps {
  callInfo: IncomingCallInfo
  onAnswer: () => void
  onDecline: () => void
}

export function IncomingCallPopup({ callInfo, onAnswer, onDecline }: IncomingCallPopupProps) {
  const t = useTranslations("telephony")
  const openLead = () => {
    if (callInfo.lead?.id) {
      window.open(`/leads/${callInfo.lead.id}`, "_blank")
    }
  }

  return (
    <div className="fixed bottom-6 right-6 z-50 w-80 rounded-xl border bg-background shadow-2xl overflow-hidden">
      {/* Animated ring pulse bar */}
      <div className="h-1 bg-green-500 animate-pulse" />

      <div className="p-4 space-y-3">
        {/* Header */}
        <div className="flex items-center gap-2">
          <div className="relative">
            <div className="h-10 w-10 rounded-full bg-green-100 dark:bg-green-900/30 flex items-center justify-center">
              <IconPhoneCall className="h-5 w-5 text-green-600 dark:text-green-400 animate-pulse" />
            </div>
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-xs font-medium text-green-600 dark:text-green-400 uppercase tracking-wider">
              {t("incomingCall.title")}
            </p>
            <p className="text-lg font-semibold truncate">
              {callInfo.lead?.full_name || callInfo.number || "Unknown"}
            </p>
          </div>
        </div>

        {/* Caller details */}
        <div className="space-y-1.5 text-sm">
          <div className="flex items-center gap-2 text-muted-foreground">
            <IconPhone className="h-3.5 w-3.5" />
            <span>{callInfo.number || t("incomingCall.unknownNumber")}</span>
          </div>
          {callInfo.lead && (
            <>
              {callInfo.lastCall && (
                <div className="flex items-center gap-2 text-muted-foreground">
                  <IconUser className="h-3.5 w-3.5" />
                  <span>
                    Last call: {formatDistanceToNow(new Date(callInfo.lastCall.started_at!), { addSuffix: true })}
                  </span>
                </div>
              )}
            </>
          )}
        </div>

        {/* Actions */}
        <div className="flex gap-2">
          <Button
            className="flex-1 bg-green-600 hover:bg-green-700 text-white"
            onClick={onAnswer}
          >
            <IconPhoneCall className="h-4 w-4 mr-1.5" />
            {t("incomingCall.answer")}
          </Button>
          <Button variant="outline" className="flex-1" onClick={onDecline}>
            <IconPhoneOff className="h-4 w-4 mr-1.5" />
            {t("incomingCall.decline")}
          </Button>
          {callInfo.lead?.id && (
            <Button variant="ghost" size="icon" className="shrink-0" onClick={openLead}>
              <IconExternalLink className="h-4 w-4" />
            </Button>
          )}
        </div>
      </div>
    </div>
  )
}
